import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import type { SourceSystem } from 'eds-common';
import { useGuide } from '@knocklabs/react';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useGetMeApi } from 'eds-common/api/v2';
import {
  AccessCodeRedemptionProvider,
  useAccessCodeRedemptionContext,
} from './AccessCodeRedemptionContext';
import { STEP_IDS, stepConfigs, type StepId } from './step-configs';
import { RedeemAccessCode } from './EnterAccessCode';
import { ConfirmRedemption } from './ConfirmRedemption';
import { FirstTimeWelcomeRedemption } from './FirstTimeWelcomeRedemption';
import { AutoEntitleClasses } from './AutoEntitleClasses';
import { AutoEntitleRedemptionSuccess } from './AutoEntitleRedemptionSuccess';
import { ConfirmOrganization } from './ConfirmOrganization';
import { MyScholasticRedemptionSuccess } from './MyScholasticRedemptionSuccess';
import type {
  AccessCodeRedemptionInitialState,
  PendingAppLaunch,
} from './access-code-deep-link';

const stepComponents: Record<StepId, ComponentType> = {
  [STEP_IDS.ENTER_CODE]: RedeemAccessCode,
  [STEP_IDS.WELCOME_REDEMPTION]: FirstTimeWelcomeRedemption,
  [STEP_IDS.CONFIRM]: ConfirmRedemption,
  [STEP_IDS.AUTO_ENTITLE]: AutoEntitleClasses,
  [STEP_IDS.CONFIRM_ORG]: ConfirmOrganization,
  [STEP_IDS.SUCCESS_AUTO_ENTITLE]: AutoEntitleRedemptionSuccess,
  [STEP_IDS.SUCCESS_MYSCHOLASTIC]: MyScholasticRedemptionSuccess,
};

function AccessCodeRedemptionDialogContent({
  trigger,
}: {
  trigger?: ReactNode;
}) {
  const { currentStepId, isDialogOpen, setIsDialogOpen } =
    useAccessCodeRedemptionContext();

  const currentConfig = stepConfigs[currentStepId];

  if (!currentConfig) {
    return null;
  }

  const StepComponent = stepComponents[currentStepId];

  const { hideStandardHeader } = currentConfig;

  return (
    <RadixDialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-black/75" />
        <RadixDialog.Content
          className={
            hideStandardHeader
              ? 'fixed left-1/2 top-1/2 w-[calc(100vw-3rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-sm bg-brand-white tablet:max-w-[500px] laptop:max-w-[700px]'
              : 'fixed left-1/2 top-1/2 w-[calc(100vw-3rem)] -translate-x-1/2 -translate-y-1/2 rounded-sm bg-brand-white p-8 pt-10 tablet:max-w-[500px] laptop:max-w-[700px]'
          }
          {...(!currentConfig.description && { 'aria-describedby': undefined })}
          data-testid="access-code-redemption-dialog"
        >
          {hideStandardHeader ? (
            <>
              <RadixDialog.Title className="sr-only">
                {currentConfig.title}
              </RadixDialog.Title>
              <StepComponent />
            </>
          ) : (
            <>
              <RadixDialog.Title className="text-xl font-semibold text-neutral-1300">
                {currentConfig.title}
              </RadixDialog.Title>
              {currentConfig.description && (
                <RadixDialog.Description className="mb-4 text-neutral-900">
                  {currentConfig.description}
                </RadixDialog.Description>
              )}
              <div className="mt-8 inline-block w-full">
                <StepComponent />
              </div>
            </>
          )}
          <RadixDialog.Close
            aria-label="Close access code redemption dialog"
            className="absolute right-4 top-4 flex size-6 items-center justify-center rounded-full border-none bg-white text-neutral-1100 focus-ring hover:bg-neutral-100"
          >
            <Cross2Icon className="size-4" aria-hidden />
          </RadixDialog.Close>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

type GuideClient = ReturnType<typeof useGuide>['client'];

function checkSettled(client: GuideClient | undefined) {
  if (!client?.store) {
    return true;
  }
  const queries = Object.values(client.store.state.queries);
  if (queries.length === 0) {
    return false;
  }
  return queries.every((q) => q.status !== 'loading');
}

function useGuideQuerySettled(client: GuideClient | undefined) {
  const [settled, setSettled] = useState(() => checkSettled(client));

  useEffect(() => {
    if (!client?.store) {
      setSettled(true);
      return undefined;
    }
    if (settled) {
      return undefined;
    }

    const update = () => {
      if (checkSettled(client)) {
        setSettled(true);
      }
    };
    update();
    return client.store.subscribe(update);
  }, [client, settled]);

  return settled;
}

export function AccessCodeRedemptionDialog({
  trigger,
  open,
  onOpenChange,
  initialState,
  pendingAppLaunch,
}: {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialState?: AccessCodeRedemptionInitialState | null;
  pendingAppLaunch?: PendingAppLaunch | null;
}) {
  const { data: user } = useGetMeApi();
  const { client: guideClient, step: welcomeGuideStep } = useGuide({
    key: 'welcome-modal',
  });

  const isEducator =
    user?.currentRole === 'teacher' || user?.currentRole === 'administrator';

  const userSource = user?.userSource as SourceSystem;
  const isFirstTime = Boolean(welcomeGuideStep);

  // Deep-link arrivals pick their initial step from isFirstTime, which is
  // derived from Knock's welcome-modal guide. Until that query settles we
  // can't answer "first-time or returning?" — defer mounting the provider
  // so it initializes against a correct, stable value.
  const isGuideSettled = useGuideQuerySettled(guideClient);
  const shouldGateInitialOpen = Boolean(initialState) && !isGuideSettled;

  if (!isEducator || !userSource) {
    return null;
  }

  if (shouldGateInitialOpen) {
    return null;
  }

  return (
    <AccessCodeRedemptionProvider
      userSource={userSource}
      isFirstTime={isFirstTime}
      externalOpen={open}
      externalOnOpenChange={onOpenChange}
      initialState={initialState}
      pendingAppLaunch={pendingAppLaunch}
    >
      <AccessCodeRedemptionDialogContent trigger={trigger} />
    </AccessCodeRedemptionProvider>
  );
}
